import type { UnixDateRange } from '~/components/features/TradeHistoryTable/utils/dateRange'

export const responsiveColumnHistoryIds = [
  'order',
  'symbol',
  'open_date',
  'close_date',
  'type',
  'vol',
  'price',
  'tp',
  'sl',
  // 'comission',
  'swap',
  'realized_pnl'
] as const

export type ResponsiveColumnHistoryId = (typeof responsiveColumnHistoryIds)[number]

export type { UnixDateRange }

function isResponsiveColumnHistoryId(value: string): value is ResponsiveColumnHistoryId {
  return responsiveColumnHistoryIds.includes(value as ResponsiveColumnHistoryId)
}

export const useTradeHistoryTableStore = defineStore('trade-history-table', () => {
  const OTHER_COLUMN_WIDTH = 85.14
  const MIN_VISIBLE_RESPONSIVE_COLUMNS = 2

  const { t } = useI18n()

  const layoutWidth = ref(0)

  const openDateRange = ref<UnixDateRange | null>(null)
  const closeDateRange = ref<UnixDateRange | null>(null)

  function resetOpenDateRange() {
    openDateRange.value = null
  }

  function resetCloseDateRange() {
    closeDateRange.value = null
  }

  function createDefaultColumnVisibility(): Record<ResponsiveColumnHistoryId, boolean> {
    return responsiveColumnHistoryIds.reduce(
      (visibility, columnId) => {
        visibility[columnId] = true

        return visibility
      },
      {} as Record<ResponsiveColumnHistoryId, boolean>
    )
  }

  function normalizeResponsiveColumnOrder(columnOrder: ResponsiveColumnHistoryId[]) {
    const validColumnIds = columnOrder.filter(isResponsiveColumnHistoryId)
    const uniqueColumnIds = Array.from(new Set(validColumnIds))
    const missingColumnIds = responsiveColumnHistoryIds.filter((columnId) => {
      return !uniqueColumnIds.includes(columnId)
    })

    return [...uniqueColumnIds, ...missingColumnIds]
  }

  function normalizeColumnVisibility(
    columnVisibility: Partial<Record<ResponsiveColumnHistoryId, boolean>>
  ) {
    const defaultVisibility = createDefaultColumnVisibility()

    return responsiveColumnHistoryIds.reduce(
      (visibility, columnId) => {
        visibility[columnId] = columnVisibility[columnId] ?? defaultVisibility[columnId]

        return visibility
      },
      {} as Record<ResponsiveColumnHistoryId, boolean>
    )
  }

  const responsiveColumnOrder = useLocalStorage<ResponsiveColumnHistoryId[]>(
    'trade-history-table:responsive-column-order',
    [...responsiveColumnHistoryIds]
  )

  const userColumnVisibility = useLocalStorage<Record<ResponsiveColumnHistoryId, boolean>>(
    'trade-history-table:user-column-visibility',
    createDefaultColumnVisibility()
  )

  responsiveColumnOrder.value = normalizeResponsiveColumnOrder(responsiveColumnOrder.value)
  userColumnVisibility.value = normalizeColumnVisibility(userColumnVisibility.value)

  const columnLabels = computed<Record<ResponsiveColumnHistoryId, string>>(() => ({
    order: String(t('historyTable.order')),
    symbol: String(t('historyTable.symbol')),
    open_date: String(t('historyTable.openDate')),
    close_date: String(t('historyTable.closeDate')),
    type: String(t('historyTable.type')),
    vol: String(t('historyTable.volumeFull')),
    price: String(t('historyTable.price')),
    tp: String(t('historyTable.tp')),
    sl: String(t('historyTable.sl')),
    // comission: String(t('historyTable.comission')),
    swap: String(t('historyTable.swap')),
    realized_pnl: String(t('historyTable.realizedPnl'))
  }))

  const selectedResponsiveColumnHistoryIds = computed(() => {
    return responsiveColumnOrder.value.filter((id) => userColumnVisibility.value[id])
  })

  const visibleResponsiveColumnsLimit = computed(() => {
    const width = layoutWidth.value

    if (!width) {
      return responsiveColumnHistoryIds.length
    }

    const columnsByWidth = Math.floor(width / OTHER_COLUMN_WIDTH)

    return Math.max(
      MIN_VISIBLE_RESPONSIVE_COLUMNS,
      Math.min(responsiveColumnHistoryIds.length, columnsByWidth)
    )
  })

  const visibleResponsiveColumnHistoryIds = computed(() => {
    return selectedResponsiveColumnHistoryIds.value.slice(0, visibleResponsiveColumnsLimit.value)
  })

  const columnVisibility = computed<Record<string, boolean>>(() => {
    const visibleSet = new Set(visibleResponsiveColumnHistoryIds.value)

    return Object.fromEntries(responsiveColumnHistoryIds.map((id) => [id, visibleSet.has(id)]))
  })

  const columnOrder = computed<string[]>({
    get() {
      return responsiveColumnOrder.value
    },
    set(value) {
      responsiveColumnOrder.value = normalizeResponsiveColumnOrder(
        value.filter(isResponsiveColumnHistoryId)
      )
    }
  })

  function setLayoutWidth(width: number) {
    layoutWidth.value = width
  }

  function setResponsiveColumnOrder(order: ResponsiveColumnHistoryId[]) {
    responsiveColumnOrder.value = normalizeResponsiveColumnOrder(order)
  }

  function isResponsiveColumnVisible(id: ResponsiveColumnHistoryId) {
    return userColumnVisibility.value[id] ?? true
  }

  const setResponsiveColumnVisible = (id: ResponsiveColumnHistoryId, value: boolean) => {
    userColumnVisibility.value = {
      ...userColumnVisibility.value,
      [id]: value
    }
  }

  const getResponsiveColumnLabel = (id: ResponsiveColumnHistoryId) => {
    return columnLabels.value[id]
  }

  return {
    openDateRange,
    closeDateRange,
    resetOpenDateRange,
    resetCloseDateRange,

    columnOrder,
    columnVisibility,

    responsiveColumnHistoryIds,
    responsiveColumnOrder,
    columnLabels,
    userColumnVisibility,

    layoutWidth,
    setLayoutWidth,

    setResponsiveColumnOrder,
    isResponsiveColumnVisible,
    setResponsiveColumnVisible,
    getResponsiveColumnLabel
  }
})
